import { useState, useEffect, useMemo } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate, Link } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";
import { ClassesAPI, SectionsAPI, InstitutionsAPI } from "@/lib/sprint2-api";
import { AttendanceAPI, SubjectsAPI } from "@/lib/sprint5-api";
import { StudentsAPI } from "@/lib/sprint3-api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Loader2, CalendarCheck, Users, TrendingDown, Check, X, Clock, Send, Lock,
  ChevronRight, LayoutGrid, Calendar as CalendarIcon, Sparkles, Search, ChevronLeft,
} from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const yesterday = () => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); };

const CT_ROLES = ["class_teacher"];
const MARK_ROLES = ["super_admin", "tenant_admin", "inst_admin", "campus_admin", "principal", "hod", "coordinator", "teacher", "class_teacher"];

export default function Attendance() {
  const { user } = useAuth();
  const nav = useNavigate();
  const roles = user?.roles || [];
  const isCT = roles.some((r) => CT_ROLES.includes(r));
  const canMark = roles.some((r) => MARK_ROLES.includes(r));
  const [activeTab, setActiveTab] = useState("overview");
  const [rollCallSection, setRollCallSection] = useState(null);
  const [selectedDate, setSelectedDate] = useState(today());

  const instQ = useQuery({ queryKey: ["att-insts"], queryFn: () => InstitutionsAPI.list() });
  const instId = user?.institution_id || instQ.data?.[0]?.id;
  const myClassesQ = useQuery({
    queryKey: ["att-my-classes"],
    queryFn: () => SectionsAPI.myClasses(),
    enabled: isCT,
  });

  return (
    <AppShell>
      <div className="space-y-5" data-testid="att-page">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <h1 className="font-display font-black tracking-tight text-3xl">Attendance</h1>
            <p className="text-sm text-muted-foreground max-w-2xl mt-1">
              Daily roll call, subject-period attendance, and long-term reports for every section.
            </p>
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-4" data-testid="att-tabs">
            <TabsTrigger value="overview" data-testid="att-tab-overview">
              <LayoutGrid className="h-3.5 w-3.5 mr-1.5" /> Overview
            </TabsTrigger>
            <TabsTrigger value="rollcall" data-testid="att-tab-rollcall">
              <CalendarCheck className="h-3.5 w-3.5 mr-1.5" /> Mark Attendance
            </TabsTrigger>
            <TabsTrigger value="calendar" data-testid="att-tab-calendar">
              <CalendarIcon className="h-3.5 w-3.5 mr-1.5" /> Calendar
            </TabsTrigger>
            <TabsTrigger value="report" data-testid="att-tab-report">
              <Users className="h-3.5 w-3.5 mr-1.5" /> Class Report
            </TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <OverviewTab
              instId={instId}
              myClasses={myClassesQ.data || []}
              isCT={isCT}
              canMark={canMark}
              onStartRollCall={(sec) => { setRollCallSection(sec); setSelectedDate(today()); setActiveTab("rollcall"); }}
            />
          </TabsContent>

          <TabsContent value="rollcall">
            <RollCallTab
              instId={instId}
              myClasses={myClassesQ.data || []}
              defaultSection={rollCallSection}
              defaultDate={selectedDate}
              onDateChange={setSelectedDate}
            />
          </TabsContent>

          <TabsContent value="calendar">
            <CalendarTab instId={instId} myClasses={myClassesQ.data || []} />
          </TabsContent>

          <TabsContent value="report">
            <ReportTab instId={instId} myClasses={myClassesQ.data || []} />
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  );
}

// ─────────────────────────────────────────────────────────────────
// OVERVIEW — KPIs + "Take Roll Call" CTA
// ─────────────────────────────────────────────────────────────────
function OverviewTab({ instId, myClasses, isCT, canMark, onStartRollCall }) {
  const sessionsQ = useQuery({
    queryKey: ["att-today-sessions", today()],
    queryFn: () => AttendanceAPI.listSessions({ date_from: today(), date_to: today() }),
  });
  const sessions = sessionsQ.data || [];
  const openSessions = sessions.filter((s) => s.status === "open");
  const closedSessions = sessions.filter((s) => s.status === "closed");

  return (
    <div className="space-y-4">
      {/* Hero: CT quick actions */}
      {isCT && myClasses.length > 0 && (
        <div className="rounded-xl border border-indigo-200 bg-gradient-to-br from-indigo-50 to-white p-5" data-testid="att-ct-hero">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-semibold text-indigo-700 flex items-center gap-1.5 mb-1">
                <Sparkles className="h-3 w-3" /> Class Teacher Quick Roll Call
              </div>
              <h2 className="font-display font-bold text-lg">
                Mark today&apos;s homeroom attendance for your section{myClasses.length > 1 ? "s" : ""}
              </h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {myClasses.map((sec) => (
                <Button
                  key={sec.id}
                  onClick={() => onStartRollCall(sec)}
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid={`att-ct-quick-${sec.id}`}
                >
                  Section {sec.name} <ChevronRight className="h-3.5 w-3.5 ml-1" />
                </Button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi icon={<CalendarCheck className="h-4 w-4" />} label="Sessions today" value={sessions.length} tint="slate" testid="att-kpi-today" />
        <Kpi icon={<Clock className="h-4 w-4" />} label="Pending (open)" value={openSessions.length} tint="amber" testid="att-kpi-pending" />
        <Kpi icon={<Check className="h-4 w-4" />} label="Closed today" value={closedSessions.length} tint="emerald" testid="att-kpi-closed" />
        <Kpi icon={<TrendingDown className="h-4 w-4" />} label="Chronic (< 75%)" value={0} tint="red" testid="att-kpi-chronic" />
      </div>

      {/* Today's sessions */}
      <div className="border border-border rounded-lg bg-card">
        <div className="px-4 py-3 border-b border-border flex items-center justify-between">
          <h3 className="font-semibold text-sm">Today&apos;s sessions</h3>
          <span className="text-xs text-muted-foreground">{sessions.length} in total</span>
        </div>
        {sessionsQ.isLoading ? (
          <div className="p-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-muted-foreground" /></div>
        ) : sessions.length === 0 ? (
          <div className="p-10 text-center text-xs text-muted-foreground">
            No sessions started today.{canMark && " Use \"Mark Attendance\" to start one."}
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {sessions.map((s) => (
              <li key={s.id} className="px-4 py-3 flex items-center justify-between gap-2 hover:bg-muted/30" data-testid={`att-session-${s.id}`}>
                <div className="min-w-0">
                  <div className="text-sm font-medium">
                    {s.class_name || "Class"}{s.section_name ? ` · ${s.section_name}` : ""}
                    {s.subject_name && <span className="text-muted-foreground"> · {s.subject_name}</span>}
                  </div>
                  <div className="text-[11px] text-muted-foreground">
                    {s.date} · {s.period || "Homeroom"} · {s.total_marked || 0} of {s.total_students || 0} marked
                  </div>
                </div>
                <Badge className={s.status === "closed" ? "bg-emerald-100 text-emerald-700 border-emerald-200" : "bg-amber-100 text-amber-700 border-amber-200"}>
                  {s.status === "closed" ? <><Lock className="h-3 w-3 mr-1" /> Closed</> : "Open"}
                </Badge>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// ROLL CALL — the actual marking screen
// ─────────────────────────────────────────────────────────────────
function RollCallTab({ instId, myClasses, defaultSection, defaultDate, onDateChange }) {
  const [classId, setClassId] = useState(defaultSection?.class_id || "");
  const [sectionId, setSectionId] = useState(defaultSection?.id || "");
  const [subjectId, setSubjectId] = useState("");
  const [date, setDate] = useState(defaultDate || today());
  const [session, setSession] = useState(null);
  const [marks, setMarks] = useState({}); // {student_id: status}
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (defaultSection) { setClassId(defaultSection.class_id); setSectionId(defaultSection.id); } }, [defaultSection]);

  const classesQ = useQuery({
    queryKey: ["rc-classes", instId],
    queryFn: () => ClassesAPI.list({ institution_id: instId }),
    enabled: !!instId,
  });
  const sectionsQ = useQuery({
    queryKey: ["rc-sections", classId],
    queryFn: () => SectionsAPI.list({ class_id: classId }),
    enabled: !!classId,
  });
  const subjectsQ = useQuery({
    queryKey: ["rc-subjects", classId],
    queryFn: () => SubjectsAPI.list({ class_id: classId }),
    enabled: !!classId,
  });
  const studentsQ = useQuery({
    queryKey: ["rc-students", classId, sectionId],
    queryFn: () => StudentsAPI.list({ class_id: classId, section_id: sectionId }),
    enabled: !!(classId && sectionId),
  });

  const isPastDate = date < today();
  const isToday = date === today();

  // Load or auto-create session when class+section+date are all set.
  useEffect(() => {
    if (!classId || !sectionId || !date) return;
    let cancel = false;
    (async () => {
      try {
        const existing = await AttendanceAPI.listSessions({
          class_id: classId, section_id: sectionId, date_from: date, date_to: date,
          ...(subjectId ? { subject_id: subjectId } : {}),
        });
        if (cancel) return;
        if (existing?.length) {
          const s = await AttendanceAPI.getSession(existing[0].id);
          setSession(s);
          const m = {};
          (s.records || []).forEach((r) => { m[r.student_id] = r.status; });
          setMarks(m);
        } else {
          setSession(null); setMarks({});
        }
      } catch (e) { /* ignore */ }
    })();
    return () => { cancel = true; };
  }, [classId, sectionId, subjectId, date]);

  const startSession = async () => {
    setSaving(true);
    try {
      const s = await AttendanceAPI.createSession({
        class_id: classId,
        section_id: sectionId,
        subject_id: subjectId || null,
        date,
        period: subjectId ? "Period" : "Homeroom",
      });
      setSession(await AttendanceAPI.getSession(s.id));
      toast.success("Session started");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to start session"); }
    finally { setSaving(false); }
  };

  const students = studentsQ.data || [];
  const markedCount = Object.values(marks).filter(Boolean).length;
  const setAll = (status) => {
    const m = {};
    students.forEach((st) => { m[st.id] = status; });
    setMarks(m);
  };
  const setOne = (sid, status) => setMarks((p) => ({ ...p, [sid]: status }));

  const save = async () => {
    if (!session) return;
    setSaving(true);
    try {
      const records = Object.entries(marks).filter(([_, v]) => v).map(([student_id, status]) => ({ student_id, status }));
      await AttendanceAPI.mark(session.id, records);
      toast.success(`${records.length} student${records.length === 1 ? "" : "s"} marked`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };

  const closeSession = async () => {
    if (!session) return;
    if (!window.confirm("Close this session? Absent-alerts will be dispatched and further edits will need a regularisation.")) return;
    setSaving(true);
    try {
      await save();
      await AttendanceAPI.close(session.id);
      toast.success("Session closed & alerts sent");
      const s = await AttendanceAPI.getSession(session.id);
      setSession(s);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Close failed"); }
    finally { setSaving(false); }
  };

  const copyYesterday = async () => {
    try {
      const yList = await AttendanceAPI.listSessions({ class_id: classId, section_id: sectionId, date_from: yesterday(), date_to: yesterday() });
      if (!yList?.length) { toast.error("No session found for yesterday"); return; }
      const y = await AttendanceAPI.getSession(yList[0].id);
      const m = {};
      (y.records || []).forEach((r) => { if (r.status) m[r.student_id] = r.status; });
      setMarks((p) => ({ ...p, ...m }));
      toast.success(`Copied ${Object.keys(m).length} marks from yesterday`);
    } catch { toast.error("Could not copy yesterday's session"); }
  };

  const closed = session?.status === "closed";
  const locked = closed || (isPastDate && !session);

  return (
    <div className="space-y-4">
      {/* Selectors */}
      <div className="bg-card border border-border rounded-lg p-3 grid grid-cols-1 md:grid-cols-5 gap-2" data-testid="rc-selectors">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Class</label>
          <Select value={classId} onValueChange={(v) => { setClassId(v); setSectionId(""); setSubjectId(""); }}>
            <SelectTrigger data-testid="rc-class"><SelectValue placeholder="Pick class" /></SelectTrigger>
            <SelectContent>
              {(classesQ.data || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Section</label>
          <Select value={sectionId} onValueChange={setSectionId} disabled={!classId}>
            <SelectTrigger data-testid="rc-section"><SelectValue placeholder="Pick section" /></SelectTrigger>
            <SelectContent>
              {(sectionsQ.data || []).map((s) => <SelectItem key={s.id} value={s.id}>Section {s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Subject</label>
          <Select value={subjectId || "none"} onValueChange={(v) => setSubjectId(v === "none" ? "" : v)} disabled={!classId}>
            <SelectTrigger data-testid="rc-subject"><SelectValue placeholder="Homeroom" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">— Homeroom (no subject) —</SelectItem>
              {(subjectsQ.data || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Date</label>
          <Input type="date" value={date} onChange={(e) => { setDate(e.target.value); onDateChange?.(e.target.value); }} max={today()} data-testid="rc-date" />
        </div>
        <div className="flex items-end">
          {!session && classId && sectionId && !isPastDate && (
            <Button onClick={startSession} disabled={saving} className="w-full" data-testid="rc-start-session">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Start Session"}
            </Button>
          )}
        </div>
      </div>

      {/* Status banner */}
      {isPastDate && !closed && (
        <div className="p-3 rounded-md bg-amber-50 border border-amber-200 text-xs text-amber-900">
          <Lock className="h-3 w-3 inline mr-1" /> Past date — marks are read-only. Admins can request a regularisation to amend.
        </div>
      )}
      {closed && (
        <div className="p-3 rounded-md bg-emerald-50 border border-emerald-200 text-xs text-emerald-900">
          <Lock className="h-3 w-3 inline mr-1" /> Session closed. Absent-alerts have been dispatched.
        </div>
      )}

      {/* Marking body */}
      {session && (
        <>
          {/* Bulk toolbar */}
          <div className="bg-card border border-border rounded-lg p-3 flex items-center gap-2 flex-wrap" data-testid="rc-toolbar">
            <span className="text-xs text-muted-foreground font-semibold mr-2">Bulk:</span>
            <Button size="sm" variant="outline" onClick={() => setAll("present")} disabled={locked} className="text-emerald-700 border-emerald-300" data-testid="rc-all-present">
              <Check className="h-3.5 w-3.5 mr-1" /> All Present
            </Button>
            <Button size="sm" variant="outline" onClick={() => setAll("absent")} disabled={locked} className="text-rose-700 border-rose-300">
              <X className="h-3.5 w-3.5 mr-1" /> All Absent
            </Button>
            <Button size="sm" variant="outline" onClick={copyYesterday} disabled={locked}>
              <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Copy yesterday
            </Button>
            <div className="ml-auto flex items-center gap-2">
              <span className="text-xs text-muted-foreground" data-testid="rc-progress">
                {markedCount} / {students.length} marked
              </span>
              {isToday && !closed && (
                <>
                  <Button size="sm" onClick={save} disabled={saving || markedCount === 0} data-testid="rc-save">
                    {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Save draft"}
                  </Button>
                  <Button size="sm" onClick={closeSession} disabled={saving || markedCount === 0} className="bg-indigo-600 hover:bg-indigo-700" data-testid="rc-close">
                    <Send className="h-3.5 w-3.5 mr-1" /> Save &amp; Close
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Student rows */}
          <div className="border border-border rounded-lg bg-card divide-y divide-border" data-testid="rc-students">
            {students.map((st, idx) => (
              <StudentRow
                key={st.id}
                idx={idx}
                student={st}
                status={marks[st.id]}
                locked={locked}
                onSet={(s) => setOne(st.id, s)}
              />
            ))}
            {students.length === 0 && studentsQ.isLoading && (
              <div className="p-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-muted-foreground" /></div>
            )}
            {students.length === 0 && !studentsQ.isLoading && (
              <div className="p-10 text-center text-xs text-muted-foreground">No students enrolled in this section.</div>
            )}
          </div>
        </>
      )}

      {!session && classId && sectionId && !isPastDate && (
        <div className="p-10 text-center border border-dashed border-border rounded-lg text-sm text-muted-foreground">
          Click <b>Start Session</b> above to begin roll call for {date}.
        </div>
      )}
    </div>
  );
}

function StudentRow({ idx, student, status, locked, onSet }) {
  const [pct, setPct] = useState(null);
  useEffect(() => {
    let cancel = false;
    (async () => {
      try {
        const s = await AttendanceAPI.studentSummary(student.id);
        if (!cancel) setPct(typeof s?.percentage === "number" ? s.percentage : null);
      } catch { /* ignore */ }
    })();
    return () => { cancel = true; };
  }, [student.id]);

  const btn = (v, label, cls) => (
    <button
      onClick={() => !locked && onSet(v)}
      disabled={locked}
      data-testid={`rc-${student.id}-${v}`}
      className={`px-3 py-1.5 rounded font-bold text-sm border transition ${status === v ? cls : "bg-white text-muted-foreground border-border hover:border-primary"} disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {label}
    </button>
  );

  const initials = `${student.first_name?.[0] || ""}${student.last_name?.[0] || ""}`.toUpperCase() || "?";

  return (
    <div className="px-3 py-2.5 flex items-center gap-3 hover:bg-muted/20" data-testid={`rc-row-${student.id}`}>
      <span className="text-xs text-muted-foreground font-mono w-6 text-center">{idx + 1}</span>
      <Link to={`/students/${student.id}`} className="h-8 w-8 rounded-full bg-slate-100 text-slate-700 font-semibold flex items-center justify-center text-xs hover:ring-2 hover:ring-primary transition">
        {initials}
      </Link>
      <Link to={`/students/${student.id}`} className="flex-1 min-w-0 hover:underline">
        <div className="text-sm font-medium truncate">{student.first_name} {student.last_name}</div>
        <div className="text-[11px] text-muted-foreground font-mono">{student.admission_no}</div>
      </Link>
      {pct !== null && (
        <Badge variant="outline" className={`text-[10px] ${pct < 75 ? "border-rose-300 text-rose-700 bg-rose-50" : pct < 90 ? "border-amber-300 text-amber-700" : "border-emerald-300 text-emerald-700"}`}>
          {Math.round(pct)}%
        </Badge>
      )}
      <div className="flex items-center gap-1.5">
        {btn("present", "P", "bg-emerald-600 text-white border-emerald-700")}
        {btn("late", "L", "bg-amber-500 text-white border-amber-600")}
        {btn("absent", "A", "bg-rose-600 text-white border-rose-700")}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// CALENDAR — monthly heatmap for a chosen section
// ─────────────────────────────────────────────────────────────────
function CalendarTab({ instId, myClasses }) {
  const [classId, setClassId] = useState(myClasses[0]?.class_id || "");
  const [sectionId, setSectionId] = useState(myClasses[0]?.id || "");
  const [ym, setYm] = useState(() => today().slice(0, 7));

  const classesQ = useQuery({
    queryKey: ["cal-classes", instId],
    queryFn: () => ClassesAPI.list({ institution_id: instId }),
    enabled: !!instId,
  });
  const sectionsQ = useQuery({
    queryKey: ["cal-sections", classId],
    queryFn: () => SectionsAPI.list({ class_id: classId }),
    enabled: !!classId,
  });
  const start = `${ym}-01`;
  const [year, mon] = ym.split("-").map(Number);
  const daysInMonth = new Date(year, mon, 0).getDate();
  const end = `${ym}-${String(daysInMonth).padStart(2, "0")}`;

  const sessionsQ = useQuery({
    queryKey: ["cal-sessions", classId, sectionId, ym],
    queryFn: () => AttendanceAPI.listSessions({ class_id: classId, section_id: sectionId, date_from: start, date_to: end }),
    enabled: !!(classId && sectionId),
  });

  const byDate = useMemo(() => {
    const m = new Map();
    (sessionsQ.data || []).forEach((s) => { m.set(s.date, s); });
    return m;
  }, [sessionsQ.data]);

  const shift = (delta) => {
    const [y, mo] = ym.split("-").map(Number);
    const d = new Date(y, mo - 1 + delta, 1);
    setYm(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };
  const firstDow = new Date(year, mon - 1, 1).getDay(); // 0=Sun
  const cells = Array.from({ length: firstDow + daysInMonth }, (_, i) =>
    i < firstDow ? null : `${ym}-${String(i - firstDow + 1).padStart(2, "0")}`
  );

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-lg p-3 grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Class</label>
          <Select value={classId} onValueChange={(v) => { setClassId(v); setSectionId(""); }}>
            <SelectTrigger><SelectValue placeholder="Pick class" /></SelectTrigger>
            <SelectContent>
              {(classesQ.data || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Section</label>
          <Select value={sectionId} onValueChange={setSectionId} disabled={!classId}>
            <SelectTrigger><SelectValue placeholder="Pick section" /></SelectTrigger>
            <SelectContent>
              {(sectionsQ.data || []).map((s) => <SelectItem key={s.id} value={s.id}>Section {s.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-center gap-2 justify-end">
          <Button variant="outline" size="sm" onClick={() => shift(-1)}><ChevronLeft className="h-3.5 w-3.5" /></Button>
          <span className="text-sm font-semibold w-28 text-center">{new Date(start).toLocaleString(undefined, { month: "long", year: "numeric" })}</span>
          <Button variant="outline" size="sm" onClick={() => shift(1)}><ChevronRight className="h-3.5 w-3.5" /></Button>
        </div>
      </div>

      {!classId || !sectionId ? (
        <div className="p-10 text-center text-xs text-muted-foreground border border-dashed border-border rounded-lg">Pick a class and section to view the calendar.</div>
      ) : (
        <div className="bg-card border border-border rounded-lg p-4">
          <div className="grid grid-cols-7 gap-1 mb-2 text-[10px] uppercase tracking-wider font-semibold text-muted-foreground text-center">
            {["S","M","T","W","T","F","S"].map((d, i) => <div key={i}>{d}</div>)}
          </div>
          <div className="grid grid-cols-7 gap-1" data-testid="cal-grid">
            {cells.map((iso, i) => {
              if (!iso) return <div key={i} className="h-16" />;
              const s = byDate.get(iso);
              const closed = s?.status === "closed";
              const cls = closed ? "bg-emerald-50 border-emerald-200"
                       : s ? "bg-amber-50 border-amber-200"
                       : "bg-white border-border";
              return (
                <div key={iso} className={`h-16 border rounded-md p-1.5 text-[10px] ${cls}`} data-testid={`cal-day-${iso}`}>
                  <div className="font-mono font-semibold">{iso.slice(8)}</div>
                  {s && (
                    <div className="mt-1">
                      <div className={closed ? "text-emerald-700 font-semibold" : "text-amber-700"}>
                        {closed ? "Closed" : "Open"}
                      </div>
                      <div className="text-muted-foreground truncate">{s.total_marked || 0}/{s.total_students || 0}</div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          <div className="flex items-center gap-4 mt-4 text-[10px] text-muted-foreground">
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-emerald-50 border border-emerald-200 inline-block" />Closed & sent</span>
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-amber-50 border border-amber-200 inline-block" />Open (unclosed)</span>
            <span className="flex items-center gap-1.5"><span className="h-3 w-3 rounded bg-white border border-border inline-block" />No session</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// CLASS REPORT — class summary + by-section table + per-student list
// ─────────────────────────────────────────────────────────────────
function ReportTab({ instId, myClasses }) {
  const [classId, setClassId] = useState(myClasses[0]?.class_id || "");
  const [q, setQ] = useState("");
  const classesQ = useQuery({
    queryKey: ["rep-classes", instId],
    queryFn: () => ClassesAPI.list({ institution_id: instId }),
    enabled: !!instId,
  });
  const reportQ = useQuery({
    queryKey: ["rep-report", classId],
    queryFn: () => AttendanceAPI.classReport(classId),
    enabled: !!classId,
  });
  const studentsQ = useQuery({
    queryKey: ["rep-students", classId],
    queryFn: () => StudentsAPI.list({ class_id: classId }),
    enabled: !!classId,
  });

  // Per-student % — hit studentSummary in parallel with a soft cap.
  const [perStudent, setPerStudent] = useState({});
  useEffect(() => {
    if (!studentsQ.data?.length) { setPerStudent({}); return; }
    let cancel = false;
    (async () => {
      const results = await Promise.allSettled(
        studentsQ.data.slice(0, 100).map((s) => AttendanceAPI.studentSummary(s.id, { class_id: classId }))
      );
      if (cancel) return;
      const m = {};
      results.forEach((r, i) => {
        if (r.status === "fulfilled") m[studentsQ.data[i].id] = r.value;
      });
      setPerStudent(m);
    })();
    return () => { cancel = true; };
  }, [studentsQ.data, classId]);

  const summary = reportQ.data || {};
  const bySection = Array.isArray(summary.by_section) ? summary.by_section : [];
  const rows = (studentsQ.data || []).filter((s) => {
    if (!q) return true;
    const t = q.toLowerCase();
    return `${s.first_name || ""} ${s.last_name || ""}`.toLowerCase().includes(t) ||
           (s.admission_no || "").toLowerCase().includes(t);
  });

  return (
    <div className="space-y-4">
      <div className="bg-card border border-border rounded-lg p-3 grid grid-cols-1 md:grid-cols-3 gap-2 items-end">
        <div>
          <label className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">Class</label>
          <Select value={classId} onValueChange={setClassId}>
            <SelectTrigger data-testid="rep-class"><SelectValue placeholder="Pick class" /></SelectTrigger>
            <SelectContent>
              {(classesQ.data || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2 relative">
          <Search className="h-3.5 w-3.5 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search student…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-8" data-testid="rep-search" />
        </div>
      </div>

      {!classId ? (
        <div className="p-10 text-center text-xs text-muted-foreground border border-dashed border-border rounded-lg">Pick a class to view its report.</div>
      ) : reportQ.isLoading ? (
        <div className="p-10 text-center"><Loader2 className="h-5 w-5 animate-spin inline text-muted-foreground" /></div>
      ) : (
        <>
          {/* Class-level KPIs */}
          {(summary.total_marks || 0) === 0 && (summary.sessions || 0) > 0 && (
            <div className="p-3 rounded-md bg-sky-50 border border-sky-200 text-xs text-sky-900" data-testid="rep-empty-marks">
              <CalendarCheck className="h-3 w-3 inline mr-1" /> {summary.sessions} session{summary.sessions === 1 ? "" : "s"} exist but no marks have been recorded yet. Head to <b>Mark Attendance</b> to start marking.
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Kpi icon={<CalendarCheck className="h-4 w-4" />} label="Sessions" value={summary.sessions || 0} tint="slate" testid="rep-kpi-sessions" />
            <Kpi icon={<Check className="h-4 w-4" />} label="Present" value={summary.counts?.present || 0} tint="emerald" testid="rep-kpi-present" />
            <Kpi icon={<X className="h-4 w-4" />} label="Absent" value={summary.counts?.absent || 0} tint="red" testid="rep-kpi-absent" />
            <Kpi icon={<TrendingDown className="h-4 w-4" />} label="Attendance %" value={`${Math.round((summary.attendance_pct || 0))}%`} tint="amber" testid="rep-kpi-pct" />
          </div>

          {/* By-section */}
          {bySection.length > 0 && (
            <div className="border border-border rounded-lg bg-card overflow-x-auto">
              <div className="px-3 py-2 border-b border-border text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">By section</div>
              <table className="w-full text-sm">
                <thead className="bg-muted/30">
                  <tr className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                    <th className="text-left px-3 py-2">Section</th>
                    <th className="text-right px-3 py-2">Sessions</th>
                    <th className="text-right px-3 py-2">Present</th>
                    <th className="text-right px-3 py-2">Absent</th>
                    <th className="text-right px-3 py-2">Late</th>
                    <th className="text-right px-3 py-2">%</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {bySection.map((sec) => (
                    <tr key={sec.section_id} data-testid={`rep-sec-${sec.section_id}`}>
                      <td className="px-3 py-2 font-semibold">Section {sec.name}</td>
                      <td className="px-3 py-2 text-right font-mono">{sec.sessions || 0}</td>
                      <td className="px-3 py-2 text-right text-emerald-700">{sec.counts?.present || 0}</td>
                      <td className="px-3 py-2 text-right text-rose-700">{sec.counts?.absent || 0}</td>
                      <td className="px-3 py-2 text-right text-amber-700">{sec.counts?.late || 0}</td>
                      <td className="px-3 py-2 text-right font-bold">{Math.round(sec.attendance_pct || 0)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Per-student list */}
          <div className="border border-border rounded-lg bg-card overflow-x-auto">
            <div className="px-3 py-2 border-b border-border text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
              Per student ({rows.length})
            </div>
            <table className="w-full text-sm">
              <thead className="bg-muted/30">
                <tr className="text-[10px] uppercase tracking-wider font-semibold text-muted-foreground">
                  <th className="text-left px-3 py-2">Student</th>
                  <th className="text-left px-3 py-2">Admission</th>
                  <th className="text-right px-3 py-2">Present</th>
                  <th className="text-right px-3 py-2">Absent</th>
                  <th className="text-right px-3 py-2">Late</th>
                  <th className="text-right px-3 py-2">Sessions</th>
                  <th className="text-right px-3 py-2">%</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((st) => {
                  const s = perStudent[st.id];
                  const pct = typeof s?.percentage === "number" ? Math.round(s.percentage) : null;
                  return (
                    <tr key={st.id} className="hover:bg-muted/20" data-testid={`rep-row-${st.id}`}>
                      <td className="px-3 py-2">
                        <Link to={`/students/${st.id}`} className="hover:underline font-medium">{st.first_name} {st.last_name}</Link>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-muted-foreground">{st.admission_no || "—"}</td>
                      <td className="px-3 py-2 text-right text-emerald-700 font-semibold">{s?.counts?.present ?? "—"}</td>
                      <td className="px-3 py-2 text-right text-rose-700 font-semibold">{s?.counts?.absent ?? "—"}</td>
                      <td className="px-3 py-2 text-right text-amber-700">{s?.counts?.late ?? "—"}</td>
                      <td className="px-3 py-2 text-right font-mono">{s?.total ?? "—"}</td>
                      <td className="px-3 py-2 text-right font-bold">
                        {pct === null ? "—" : (
                          <span className={pct < 75 ? "text-rose-600" : pct < 90 ? "text-amber-600" : "text-emerald-600"}>{pct}%</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {rows.length === 0 && (
                  <tr><td colSpan={7} className="text-center py-8 text-xs text-muted-foreground">No students found.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function Kpi({ icon, label, value, tint, testid }) {
  const tints = {
    slate: "border-slate-200 bg-slate-50 text-slate-700",
    amber: "border-amber-200 bg-amber-50 text-amber-700",
    red: "border-red-200 bg-red-50 text-red-700",
    emerald: "border-emerald-200 bg-emerald-50 text-emerald-700",
  };
  return (
    <div className={`rounded-lg border p-4 ${tints[tint] || tints.slate}`} data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-semibold flex items-center gap-1.5 opacity-80">{icon}{label}</div>
      <div className="font-display font-black text-3xl mt-1 tracking-tight">{value}</div>
    </div>
  );
}
